import type { BelegWahl, ErwartetTeil, KontoListe, KontoListeEintrag, RechnungWahl, TeilEingabe, ZeileSicht } from './types';

// Reine Hilfen der Seite „Buchen“ (ohne React): Filter, Farben, Beträge, Teilbuchungen im Editor, Kontosuche.
// Beträge immer in Cent, Vorzeichen wie die Bankzeile (+ Geld rein, − Geld raus).

// ---------- Zeilen ----------

export type Filter = 'pruefen' | 'bestaetigt' | 'alle';
export type Farbe = 'gruen' | 'gelb' | 'rot' | 'grau';

export const zuPruefen = (z: ZeileSicht) => z.status === 'offen' || z.status === 'vorschlag';
export const hatHart = (z: ZeileSicht) => z.befunde.some((b) => b.art === 'hart');

export function imFilter(z: ZeileSicht, f: Filter): boolean {
  if (f === 'pruefen') return zuPruefen(z);
  if (f === 'bestaetigt') return z.status === 'bestaetigt';
  return true;
}

// Farbstreifen: grün bestätigt, gelb Vorschlag, rot offen oder harter Befund, grau verworfen
export function farbe(z: ZeileSicht): Farbe {
  if (z.status === 'bestaetigt') return 'gruen';
  if (z.status === 'verworfen') return 'grau';
  if (z.status === 'offen' || hatHart(z)) return 'rot';
  return 'gelb';
}

export const STATUS_TEXT: Record<ZeileSicht['status'], string> = {
  offen: 'Offen',
  vorschlag: 'Vorschlag',
  bestaetigt: 'Bestätigt',
  verworfen: 'Verworfen',
};

// Ein Enter bestätigt: gültiger Vorschlag ohne harten Befund, Teile gehen genau auf
export function vollstaendig(z: ZeileSicht): boolean {
  return (
    z.status === 'vorschlag' &&
    z.teile.length > 0 &&
    !hatHart(z) &&
    z.teile.every((t) => t.gegenkonto) &&
    z.teile.reduce((s, t) => s + t.betragCents, 0) === z.betragCents
  );
}

// Nächste Zeile zum Prüfen nach der Zeile `abId` (in der Reihenfolge der Tabelle, danach wieder von vorn)
export function naechsteOffene(zeilen: ZeileSicht[], abId: string | null): string | null {
  const i = abId ? zeilen.findIndex((z) => z.id === abId) : -1;
  const reihe = [...zeilen.slice(i + 1), ...zeilen.slice(0, Math.max(i, 0))];
  return reihe.find(zuPruefen)?.id ?? null;
}

// Spalte „Buchungstext“: Lohnzeilen ohne Namen, sonst der Text der (ersten) Buchung bzw. der Gegenpartner
export function textSpalte(z: ZeileSicht): string {
  if (z.lohn === 'ja') return 'Lohnzahlung';
  if (z.lohn === 'verdacht') return 'Lohn?';
  if (z.teile.length === 1 && z.teile[0]!.buchungstext) return z.teile[0]!.buchungstext;
  return z.gegenName || z.bankText || z.verwendungszweck.slice(0, 60) || '–';
}

// ---------- Anzeige ----------

const zahl = (cents: number) =>
  (Math.abs(cents) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// „−119,00“, „+1.200,00“ (plus), „1.200,00 €“ (euro)
export function betragText(cents: number, o: { plus?: boolean; euro?: boolean } = {}): string {
  const v = cents < 0 ? '−' : cents > 0 && o.plus ? '+' : '';
  return `${v}${zahl(cents)}${o.euro ? ' €' : ''}`;
}

// „DE89 3704 0044 0532 0130 00“
export function ibanText(iban: string): string {
  return iban.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

// ---------- Teilbuchungen im Editor ----------

// Ein Teil, wie er im Editor steht. betrag: Text wie getippt, ohne Vorzeichen in Richtung der Bankzeile
// („119,00“; „-25,00“ geht in die Gegenrichtung, z. B. einbehaltene Steuer bei Zinsen). Der letzte Teil hat keinen
// eigenen Betrag: Er ist der Rest. rtRechnungId: gewählte offene Rechnung (Zahlung auf ihren Debitor), rechnungNr nur
// zur Anzeige.
export interface EntwurfTeil {
  key: string;
  gegenkonto: string;
  bu: string;
  betrag: string;
  belegId: string | null;
  belegName: string | null;
  rtRechnungId: string | null;
  rechnungNr: string | null;
  buchungstext?: string;
}

let zaehler = 0;
export const neuerKey = () => `t${++zaehler}`;

const richtung = (bankCents: number): 1 | -1 => (bankCents < 0 ? -1 : 1);

// Betrag im Editor als Text (Richtung der Bankzeile = positiv): 11900 → „119,00“
export function betragFeld(cents: number, bankCents: number): string {
  const v = cents * richtung(bankCents);
  return `${v < 0 ? '-' : ''}${zahl(v)}`;
}

// Text aus dem Betragsfeld → Cent mit dem Vorzeichen der Bankzeile; leer → null; ungültig → undefined
export function betragAusFeld(text: string, bankCents: number): number | null | undefined {
  let t = text.replace(/\s|€/g, '').replace(/^[−–]/, '-');
  if (!t) return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');
  if (!/^-?\d+(\.\d{1,2})?$/.test(t)) return undefined;
  return Math.round(Number(t) * 100) * richtung(bankCents);
}

export function entwurfAus(z: ZeileSicht): EntwurfTeil[] {
  // feste Schlüssel je Position: Neu laden (KI-Abfrage, Hintergrund) baut die Felder nicht neu, der Cursor bleibt
  if (!z.teile.length) {
    return [
      {
        key: 'b0',
        gegenkonto: '',
        bu: '',
        betrag: betragFeld(z.betragCents, z.betragCents),
        belegId: null,
        belegName: null,
        rtRechnungId: null,
        rechnungNr: null,
      },
    ];
  }
  return z.teile.map((t, i) => ({
    key: `b${i}`,
    gegenkonto: t.gegenkonto,
    bu: t.bu ?? '',
    betrag: betragFeld(t.betragCents, z.betragCents),
    belegId: t.belegId,
    belegName: t.belegName,
    rtRechnungId: t.rtRechnungId,
    // Belegfeld 1 eines Teils mit Rechnung ist ihre Nummer
    rechnungNr: t.rtRechnungId ? t.belegfeld1 : null,
    buchungstext: t.buchungstext,
  }));
}

// Beträge der Teile in Cent; der letzte Teil ist der Rest (Bankbetrag − alle anderen). null/undefined: leer/ungültig.
export function teilBetraege(teile: EntwurfTeil[], bankCents: number): (number | null | undefined)[] {
  const vorn = teile.slice(0, -1).map((t) => betragAusFeld(t.betrag, bankCents));
  if (vorn.some((c) => c == null)) return [...vorn, undefined];
  const summe = (vorn as number[]).reduce((s, c) => s + c, 0);
  return [...vorn, bankCents - summe];
}

// Teil hinzufügen: Der bisher letzte Teil behält seinen Rest als festen Betrag, der neue Teil ist der neue Rest.
// Der Beleg des vorigen Teils wird übernommen (meist dieselbe Rechnung auf zwei Konten), eine Rechnung aus dem
// Rechnungsprogramm nicht (die gehört zu einem Teil).
export function teilHinzu(teile: EntwurfTeil[], bankCents: number): EntwurfTeil[] {
  const betraege = teilBetraege(teile, bankCents);
  const letzter = teile[teile.length - 1];
  const rest = betraege[betraege.length - 1];
  const fest = teile.map((t, i) => (i === teile.length - 1 && rest != null ? { ...t, betrag: betragFeld(rest, bankCents) } : t));
  return [
    ...fest,
    {
      key: neuerKey(),
      gegenkonto: '',
      bu: '',
      betrag: '',
      belegId: letzter?.belegId ?? null,
      belegName: letzter?.belegName ?? null,
      rtRechnungId: null,
      rechnungNr: null,
    },
  ];
}

// Teil entfernen; der neue letzte Teil wird wieder zum Rest
export function teilWeg(teile: EntwurfTeil[], key: string): EntwurfTeil[] {
  const rest = teile.filter((t) => t.key !== key);
  return rest.length ? rest : teile;
}

// Geht der Betrag gegen die Richtung der Bankzeile? (Minus im Editor)
export const gegenrichtung = (cents: number | null | undefined, bankCents: number): boolean =>
  cents != null && cents !== 0 && Math.sign(cents) === -Math.sign(bankCents);

const leererTeil = (): EntwurfTeil => ({
  key: neuerKey(),
  gegenkonto: '',
  bu: '',
  betrag: '',
  belegId: null,
  belegName: null,
  rtRechnungId: null,
  rechnungNr: null,
});
const istLeer = (t: EntwurfTeil) => !t.gegenkonto.trim() && !t.bu.trim() && !t.belegId && !t.rtRechnungId;

// Rechnung für Teil i wählen (null = keine): Gegenkonto = Debitor der Rechnung, kein St-Sl, Beleg = die Rechnung im
// Ordner „Ausgangsrechnungen“. Ein Teil mit eigenem Betrag bekommt den offenen Betrag, höchstens den Bankbetrag. Der
// letzte Teil (Rest, bei einem Teil der Bankbetrag) bleibt so, wenn die Rechnung ihn ganz deckt; ist weniger offen,
// bekommt er den offenen Betrag, und ein neuer Teil mit leerem Gegenkonto nimmt den Rest (Überzahlung, andere
// Rechnung): Mehr als offen nimmt der Server auf eine Rechnung nicht an. Bleibt für einen leeren letzten Teil nichts
// mehr übrig (andere Rechnung gewählt), fällt er wieder weg.
export function rechnungSetzen(teile: EntwurfTeil[], i: number, r: RechnungWahl | null, bankCents: number): EntwurfTeil[] {
  const letzter = i === teile.length - 1;
  const neu = teile.map((t, j) => {
    if (j !== i) return t;
    if (!r) return { ...t, rtRechnungId: null, rechnungNr: null };
    return {
      ...t,
      gegenkonto: r.debitor,
      bu: '',
      buchungstext: undefined,
      rtRechnungId: r.id,
      rechnungNr: r.nummer,
      belegId: r.belegId,
      belegName: r.belegName,
      ...(!letzter ? { betrag: betragFeld(Math.min(r.offenCents, Math.abs(bankCents)) * Math.sign(bankCents), bankCents) } : {}),
    };
  });
  if (!r) return neu;
  if (letzter) {
    const rest = teilBetraege(teile, bankCents)[i];
    if (rest == null || Math.sign(rest) !== Math.sign(bankCents) || r.offenCents >= Math.abs(rest)) return neu;
    const fest = neu.map((t, j) => (j === i ? { ...t, betrag: betragFeld(r.offenCents * Math.sign(bankCents), bankCents) } : t));
    return [...fest, leererTeil()];
  }
  const betraege = teilBetraege(neu, bankCents);
  if (betraege[betraege.length - 1] === 0 && istLeer(neu[neu.length - 1]!)) return neu.slice(0, -1);
  return neu;
}

// Debitor (Personenkonto 1… bis 6…, eine Stelle länger als die Sachkonten); Kreditoren beginnen mit 7 bis 9
export const debitorForm = (nr: string) => /^[1-6]\d{4,8}$/.test(nr.trim());

// Rechnungsauswahl zeigen? Nur bei Geld rein und mit offenen Rechnungen (oder schon gewählter): wenn das Gegenkonto
// leer oder ein Debitor ist, die Zeile keinen Vorschlag hat oder die Kontierung „Rechnung wählen“ sagt
const RECHNUNG_HINWEISE = new Set(['K_rechnung_mehrere', 'K_rechnung_betrag', 'K_eingang_ohne_rechnung']);
// „Rechnung wählen“ für Teil i: Was die anderen Teile des Entwurfs schon auf eine Rechnung legen, ist dort nicht mehr
// offen (sonst bot die Liste dem Rest dieselbe Rechnung noch einmal an, und der Server lehnte ab). Die Rechnung des
// Teils selbst bleibt in der Liste.
export function rechnungenFuerTeil(rechnungen: RechnungWahl[], teile: EntwurfTeil[], i: number, bankCents: number): RechnungWahl[] {
  const betraege = teilBetraege(teile, bankCents);
  const belegt = new Map<string, number>();
  teile.forEach((t, j) => {
    if (j !== i && t.rtRechnungId) belegt.set(t.rtRechnungId, (belegt.get(t.rtRechnungId) ?? 0) + Math.max(0, betraege[j] ?? 0));
  });
  return rechnungen
    .map((r) => (belegt.has(r.id) ? { ...r, offenCents: r.offenCents - belegt.get(r.id)! } : r))
    .filter((r) => r.offenCents > 0 || r.id === teile[i]?.rtRechnungId);
}

export function rechnungZeigen(z: ZeileSicht, t: EntwurfTeil, offen: number): boolean {
  if (t.rtRechnungId) return true;
  if (z.betragCents <= 0 || offen === 0) return false;
  const gk = t.gegenkonto.trim();
  return !gk || debitorForm(gk) || z.teile.length === 0 || z.befunde.some((b) => RECHNUNG_HINWEISE.has(b.code));
}

// Der Beleg, den die Seite als Entwurf vorschlägt: nur auf einer offenen Zeile ohne Buchung (kein Vorschlag, nichts
// von Hand gespeichert) und ohne Entwurf von Hand, mit genau einem Teil ohne Beleg und einem sicheren Treffer. Ein
// Mensch, der den Beleg herausgenommen und gespeichert hat, bekommt ihn so nicht wieder untergeschoben.
export function belegVorschlag(z: ZeileSicht, teile: EntwurfTeil[], belege: BelegWahl[], handEntwurf: boolean): BelegWahl | null {
  if (z.status !== 'offen' || z.teile.length > 0 || handEntwurf) return null;
  if (teile.length !== 1 || teile[0]!.belegId) return null;
  return belege.find((b) => b.eindeutig) ?? null;
}

// „Beleg wählen“: Filter nach dem Dateinamen (alle Wörter, ohne Groß/klein und Akzente)
export function belegFilter(belege: BelegWahl[], text: string): BelegWahl[] {
  const woerter = falten(text).split(/\s+/).filter(Boolean);
  if (!woerter.length) return belege;
  return belege.filter((b) => {
    const n = falten(b.name);
    return woerter.every((w) => n.includes(w));
  });
}

// Bestätigen ohne Änderung: was die Seite von der Zeile zeigt (der Server bestätigt nur genau das)
export function erwartetAus(z: ZeileSicht): ErwartetTeil[] {
  return z.teile.map((t) => ({ gegenkonto: t.gegenkonto, bu: t.bu, betragCents: t.betragCents, belegId: t.belegId, rtRechnungId: t.rtRechnungId }));
}

const PASST_TEXT: Record<NonNullable<BelegWahl['passt']>, string> = {
  nummer_betrag: 'Nummer und Betrag passen',
  nummer: 'Nummer passt',
  betrag: 'Betrag passt',
};
export const passtText = (b: Pick<BelegWahl, 'passt'>) => (b.passt ? PASST_TEXT[b.passt] : null);

export const kontoFormOk = (nr: string) => /^\d{4,9}$/.test(nr);
// Personenkonto (Debitor) in voller Länge (Sachkontenlänge + 1); Sachkonten stehen hier immer vierstellig
export const personenkonto = (nr: string) => /^\d{5,9}$/.test(nr);

export type KontoInfo = { eintrag: KontoListeEintrag | undefined; automatik: boolean; erlaubt: string[] | null };

// Was für ein Gegenkonto gilt: Automatik (kein St-Sl), erlaubte Schlüssel (null = Konto unbekannt, alle Schlüssel)
export function kontoInfo(liste: KontoListe | undefined, nr: string): KontoInfo {
  const eintrag = liste?.konten.find((k) => k.nr === nr);
  // Debitor: nie ein St-Sl
  if (!eintrag && personenkonto(nr)) return { eintrag, automatik: false, erlaubt: [] };
  if (!eintrag) return { eintrag, automatik: false, erlaubt: null };
  return { eintrag, automatik: eintrag.automatik, erlaubt: eintrag.automatik ? [] : eintrag.bu };
}

// St-Sl passt zum Konto? (leer passt immer)
export function buErlaubt(liste: KontoListe | undefined, gegenkonto: string, bu: string): boolean {
  if (!bu) return true;
  const info = kontoInfo(liste, gegenkonto);
  if (info.erlaubt === null) return !liste || liste.bu.some((b) => b.schluessel === bu);
  return info.erlaubt.includes(bu);
}

export type Pruefung = { ok: true; teile: TeilEingabe[] } | { ok: false; fehler: string; feld: string };

// Entwurf → Eingabe für den Server, mit kurzer Meldung und dem Feld, in das der Cursor soll
export function zuEingabe(teile: EntwurfTeil[], bankCents: number, liste: KontoListe | undefined): Pruefung {
  const betraege = teilBetraege(teile, bankCents);
  const mehrere = teile.length > 1;
  const out: TeilEingabe[] = [];
  for (const [i, t] of teile.entries()) {
    const wo = mehrere ? ` (Teil ${i + 1})` : '';
    const gk = t.gegenkonto.trim();
    if (!gk) return { ok: false, fehler: `Bitte ein Gegenkonto eintragen${wo}.`, feld: `gk-${i}` };
    if (!kontoFormOk(gk)) return { ok: false, fehler: `Gegenkonto${wo}: bitte 4 bis 9 Ziffern.`, feld: `gk-${i}` };
    const bu = t.bu.trim();
    if (!buErlaubt(liste, gk, bu)) return { ok: false, fehler: `St-Sl ${bu} passt nicht zu Konto ${gk}.`, feld: `bu-${i}` };
    const c = betraege[i];
    const letzter = i === teile.length - 1;
    if (c === null) return { ok: false, fehler: `Bitte einen Betrag eintragen${wo}.`, feld: `btr-${i}` };
    if (c === undefined) {
      return letzter
        ? { ok: false, fehler: 'Bitte die Beträge der anderen Teile prüfen.', feld: `btr-${Math.max(0, betraege.findIndex((x) => x == null))}` }
        : { ok: false, fehler: `Betrag${wo} ist keine gültige Zahl.`, feld: `btr-${i}` };
    }
    // Gegenrichtung ist erlaubt (Minus), 0 nicht
    if (c === 0) {
      return letzter && mehrere
        ? { ok: false, fehler: 'Für den letzten Teil bleibt nichts übrig. Bitte einen Teil entfernen oder die Beträge prüfen.', feld: `btr-${Math.max(0, i - 1)}` }
        : { ok: false, fehler: `Betrag${wo} darf nicht 0 sein.`, feld: `btr-${i}` };
    }
    out.push({
      gegenkonto: gk,
      bu: bu || null,
      betragCents: c,
      belegId: t.belegId,
      rtRechnungId: t.rtRechnungId,
      ...(t.buchungstext !== undefined ? { buchungstext: t.buchungstext } : {}),
    });
  }
  return { ok: true, teile: out };
}

// Entwurf unverändert gegenüber der Zeile? (Schlüssel und Schreibweise der Beträge egal)
export function entwurfGleich(teile: EntwurfTeil[], z: ZeileSicht): boolean {
  const basis = entwurfAus(z);
  if (basis.length !== teile.length) return false;
  const a = teilBetraege(teile, z.betragCents);
  const b = teilBetraege(basis, z.betragCents);
  return teile.every((t, i) => {
    const o = basis[i]!;
    return (
      t.gegenkonto.trim() === o.gegenkonto &&
      t.bu.trim() === o.bu &&
      t.belegId === o.belegId &&
      t.rtRechnungId === o.rtRechnungId &&
      a[i] === b[i]
    );
  });
}

// ---------- Entwürfe im Tab ----------

// Entwürfe von Hand überstehen jeden Seitenwechsel im selben Tab (sessionStorage): ein Eintrag je Mitarbeiterin,
// Mandant und Monat, darin die Entwürfe je Zeile. Beim Speichern, Bestätigen und Abbrechen fällt der Entwurf weg.
// Nur, was getippt oder gewählt wurde: keine Dateinamen und Buchungstexte (die kommen beim Speichern vom Server).
export const entwurfSchluessel = (userId: string, clientId: string, jahr: number, monat: number) =>
  `buchen-entwurf:${userId}:${clientId}:${jahr}-${monat}`;

const ZEILE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const istText = (v: unknown): v is string => typeof v === 'string' && v.length <= 200;
const istTextOderNull = (v: unknown): v is string | null => v === null || istText(v);

// Gespeicherte Entwürfe lesen; was nicht passt, fällt weg. Jeder Teil bekommt einen neuen Schlüssel (eindeutig auf der
// Seite, auch für später hinzugefügte Teile); den Namen des Belegs zeigt die Auswahl.
export function entwuerfeLesen(gespeichert: string | null): Record<string, EntwurfTeil[]> {
  if (!gespeichert) return {};
  let roh: unknown;
  try {
    roh = JSON.parse(gespeichert);
  } catch {
    return {};
  }
  if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return {};
  const out: Record<string, EntwurfTeil[]> = {};
  for (const [id, teile] of Object.entries(roh as Record<string, unknown>)) {
    if (!ZEILE_ID.test(id) || !Array.isArray(teile) || !teile.length || teile.length > 30) continue;
    const gut = teile.every((t: unknown) => {
      if (!t || typeof t !== 'object') return false;
      const x = t as Record<string, unknown>;
      return (
        istText(x.gegenkonto) &&
        istText(x.bu) &&
        istText(x.betrag) &&
        istTextOderNull(x.belegId) &&
        istTextOderNull(x.rtRechnungId) &&
        istTextOderNull(x.rechnungNr)
      );
    });
    if (!gut) continue;
    out[id] = (teile as EntwurfTeil[]).map((t) => ({
      key: neuerKey(),
      gegenkonto: t.gegenkonto,
      bu: t.bu,
      betrag: t.betrag,
      belegId: t.belegId,
      belegName: null,
      rtRechnungId: t.rtRechnungId,
      rechnungNr: t.rechnungNr,
    }));
  }
  return out;
}

// Entwürfe zum Speichern; null: keine
export function entwuerfeText(e: Record<string, EntwurfTeil[]>): string | null {
  const ids = Object.keys(e);
  if (!ids.length) return null;
  const getippt = (t: EntwurfTeil) => ({
    gegenkonto: t.gegenkonto,
    bu: t.bu,
    betrag: t.betrag,
    belegId: t.belegId,
    rtRechnungId: t.rtRechnungId,
    rechnungNr: t.rechnungNr,
  });
  return JSON.stringify(Object.fromEntries(ids.map((id) => [id, e[id]!.map(getippt)])));
}

// ---------- Kontosuche ----------

const falten = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('de');

// Ziffern: Konten, deren Nummer so beginnt (genaue Nummer zuerst, dann Vorschlagskonten); Text: Wörter im Namen.
// Leer: die Vorschlagskonten.
export function kontoSuche(konten: KontoListeEintrag[], text: string, max = 40): KontoListeEintrag[] {
  const q = text.trim();
  const rang = (k: KontoListeEintrag) => (k.nr === q ? 0 : k.vorschlag ? 1 : 2);
  let treffer: KontoListeEintrag[];
  if (!q) treffer = konten.filter((k) => k.vorschlag);
  else if (/^\d+$/.test(q)) treffer = konten.filter((k) => k.nr.startsWith(q));
  else {
    const woerter = falten(q).split(/\s+/).filter(Boolean);
    treffer = konten.filter((k) => {
      const n = `${k.nr} ${falten(k.name)}`;
      return woerter.every((w) => n.includes(w));
    });
  }
  return treffer
    .map((k, i) => ({ k, i }))
    .sort((a, b) => rang(a.k) - rang(b.k) || a.i - b.i)
    .slice(0, max)
    .map((x) => x.k);
}

// ---------- Monat ----------

export function monatSchritt(jahr: number, monat: number, schritt: number): { jahr: number; monat: number } {
  const n = jahr * 12 + (monat - 1) + schritt;
  return { jahr: Math.floor(n / 12), monat: (n % 12) + 1 };
}

// Dateiname wie vom Server (Content-Disposition), für den Download über fetch
export function exportName(art: 'addison' | 'extf', jahr: number, monat: number): string {
  const jm = `${jahr}-${String(monat).padStart(2, '0')}`;
  return art === 'addison' ? `Buchungsliste_${jm}.csv` : `EXTF_Buchungsstapel_${jm}.csv`;
}
